import type {
  AbleitungsmodifikationenRepraesentation,
  AbleitungsmodifikationenStruktur,
  Anzahl,
  FreigabeStatus,
  SchemaElementArt,
} from "../common";
import { InternalParserError } from "../errors";

export const NAMESPACE = "urn:xoev-de:fim:standard:xdatenfelder_3.0.0";

export type Vorbefuellung = "keine" | "optional" | "verpflichtend";

export function parseVorbefuellung(value: string): Vorbefuellung {
  switch (value) {
    case "keine":
    case "optional":
    case "verpflichtend":
      return value;
    default:
      throw new InternalParserError(
        `Invalid value in <xdf:vorbefuellung>: ${value}`,
      );
  }
}

export type Datentyp =
  | "text"
  | "text_latin"
  | "date"
  | "time"
  | "datetime"
  | "bool"
  | "num"
  | "num_int"
  | "num_currency"
  | "file"
  | "obj";

export function parseDatentyp(value: string): Datentyp {
  switch (value) {
    case "text":
    case "text_latin":
    case "date":
    case "time":
    case "datetime":
    case "bool":
    case "num":
    case "num_int":
    case "num_currency":
    case "file":
    case "obj":
      return value;
    default:
      throw new InternalParserError(`Invalid value for Datentyp: ${value}`);
  }
}

export type Feldart = "input" | "select" | "label" | "hidden" | "locked";

export function parseFeldart(value: string): Feldart {
  switch (value) {
    case "input":
    case "select":
    case "label":
    case "hidden":
    case "locked":
      return value;
    default:
      throw new InternalParserError(`Invalid value for Feldart: ${value}`);
  }
}

export enum Gruppenart {
  Auswahlgruppe = "X",
}

export function parseGruppenart(value: string): Gruppenart {
  switch (value) {
    case "X":
      return Gruppenart.Auswahlgruppe;
    default:
      throw new InternalParserError(`Invalid Gruppenart: ${value}`);
  }
}

/**
 * https://www.xrepository.de/details/urn:xoev-de:fim-datenfelder:codeliste:relation
 * Version 1.0
 */
export enum RelationType {
  Abgeleitet = "ABL",
  Ersetzt = "ERS",
  Aequivalent = "EQU",
}

export function parseRelationType(value: string): RelationType {
  switch (value) {
    case "ABL":
      return RelationType.Abgeleitet;
    case "ERS":
      return RelationType.Ersetzt;
    case "EQU":
      return RelationType.Aequivalent;
    default:
      throw new InternalParserError(`Invalid value for RelationType: ${value}`);
  }
}

export enum RegelTyp {
  Komplex = "K",
  Multiplizitaet = "M",
  Validierung = "V",
  Berechnung = "B",
}

export function parseRegelTyp(value: string): RegelTyp {
  switch (value) {
    case "K":
      return RegelTyp.Komplex;
    case "M":
      return RegelTyp.Multiplizitaet;
    case "V":
      return RegelTyp.Validierung;
    case "B":
      return RegelTyp.Berechnung;
    default:
      throw new InternalParserError(`Invalid value for RegelTyp: ${value}`);
  }
}

export interface ElementIdentifikation {
  id: string;
  version?: string;
}

export interface NormReference {
  value: string;
  link?: string;
}

export interface Keyword {
  value: string;
  uri?: string;
}

export interface Relation {
  praedikat: RelationType;
  objekt: ElementIdentifikation;
}

export interface BaseData {
  identifier: string;
  id: string;
  version?: string;
  name: string;
  description?: string;
  definition?: string;
  releaseState: FreigabeStatus;
  stateSetAt?: Date;
  stateSetBy?: string;
  validSince?: Date;
  validUntil?: Date;
  versionHint?: string;
  publishedAt?: Date;
  lastChangedAt: Date;
  normReferences: NormReference[];
  relations: Relation[];
  keywords: Keyword[];
}

export interface ElementData extends BaseData {
  inputLabel: string;
  outputLabel?: string;
  elementType: SchemaElementArt;
  inputHelp?: string;
  outputHelp?: string;
}

export interface ChildRef {
  type: "dataGroup" | "dataField";
  element: ElementIdentifikation;
  anzahl: Anzahl;
  normReferences: NormReference[];
}

export interface DataGroup extends ElementData {
  art?: Gruppenart;
  rules: ElementIdentifikation[];
  children: ChildRef[];
}

export interface Constraints {
  minLength?: number;
  maxLength?: number;
  minValue?: string;
  maxValue?: string;
  pattern?: string;
  value?: string;
}

export interface SelectValue {
  code: string;
  name: string;
  help?: string;
}

export interface CodeListReference {
  canonicalIdentification?: string;
  version?: string;
  canonicalVersionUri?: string;
}

export interface DataField extends ElementData {
  inputType: Feldart;
  dataType: Datentyp;
  fillType: Vorbefuellung;
  rules: ElementIdentifikation[];
  constraints: Constraints;
  content?: string;
  codeKey?: string;
  nameKey?: string;
  helpKey?: string;
  inputSize?: number;
  mediaTypes: string[];
  maxSize?: number;
  values: SelectValue[];
  codeListReference?: CodeListReference;
}

export interface Rule {
  identifier: string;
  id: string;
  version?: string;
  name: string;
  description?: string;
  freeFormDefinition?: string;
  normReferences: NormReference[];
  keywords: Keyword[];
  creator?: string;
  lastChangedAt: Date;
  type: RegelTyp;
  // params
  // targets
  script?: string;
  // errors
}

export interface Schema extends BaseData {
  label: string;
  help?: string;
  ableitungsmodifikationenStruktur: AbleitungsmodifikationenStruktur;
  ableitungsmodifikationenRepraesentation: AbleitungsmodifikationenRepraesentation;
  dokumentsteckbrief: ElementIdentifikation;
  rules: ElementIdentifikation[];
  children: ChildRef[];
}

export type MessageHeader = {
  nachrichtId: string;
  erstellungsZeitpunkt: Date;
};

export type SchemaMessage = {
  header: MessageHeader;
  schema: Schema;

  gruppen: Record<string, DataGroup>;
  felder: Record<string, DataField>;
  regeln: Record<string, Rule>;
};
